import { Component, Input, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IFeaturePopupModel } from '@models';

// Floating "did you know" blurb. Slides up from the bottom of the window, sits for 10 seconds,
// then slides back out. The X closes it early. Setting [featurePopup] is what starts the sequence -
// the caller just decides when to hand one over.
//
// The text is rendered as html. It's global-admin authored (same trust model as the announcement
// dialog), so it's passed through the sanitizer untouched rather than being stripped.
@Component({
	selector: 'feature-popup',
	templateUrl: './feature-popup.component.html',
	styleUrl: './feature-popup.component.css',
	standalone: false,
})
export class FeaturePopupComponent implements OnDestroy {
	private static readonly VisibleMS = 10000;
	// Matches the transition duration in the css. Kept in sync by hand so the element leaves the
	// DOM only after it has finished sliding away.
	private static readonly AnimationMS = 400;

	private _featurePopup: IFeaturePopupModel;
	private hideTimer: any;
	private removeTimer: any;

	// In the DOM (so it can animate) vs. slid into view.
	isRendered: boolean = false;
	isShown: boolean = false;
	safeHtml: SafeHtml = '';

	constructor(private sanitizer: DomSanitizer) {}

	@Input() set featurePopup(value: IFeaturePopupModel) {
		this._featurePopup = value;

		if (!value?.text) return;

		this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(value.text);
		this.show();
	}
	get featurePopup(): IFeaturePopupModel {
		return this._featurePopup;
	}

	ngOnDestroy() {
		this.clearTimers();
	}

	close() {
		this.clearTimers();
		this.isShown = false;
		this.removeTimer = setTimeout(() => (this.isRendered = false), FeaturePopupComponent.AnimationMS);
	}

	private show() {
		this.clearTimers();

		// Drop any popup that's still up out of the DOM first. Re-showing an element that already
		// carries the shown class is a no-op, so without this a second popup would just swap its
		// text in place instead of animating (which the admin preview button leans on).
		this.isRendered = false;
		this.isShown = false;

		requestAnimationFrame(() => {
			// Render it off-screen, then flip the class a frame later so the browser has
			// something to transition from.
			this.isRendered = true;
			requestAnimationFrame(() => requestAnimationFrame(() => (this.isShown = true)));
		});

		this.hideTimer = setTimeout(() => this.close(), FeaturePopupComponent.VisibleMS);
	}

	private clearTimers() {
		clearTimeout(this.hideTimer);
		clearTimeout(this.removeTimer);
		this.hideTimer = null;
		this.removeTimer = null;
	}
}
